import React from 'react';
import { navigate } from 'gatsby';
import { DOCUMENTS_PANEL, DOCUMENT_ICON_PATH } from './constants';
import { trackDocumentClick } from '@src/utils/trackDocumentClick';

/**
 * Single document row in the documents panel (cheat sheet or worksheet).
 */
export function DocumentCard({
  doc,
  index,
  type,
  state,
  isLoggedIn,
  subscriptionPrice,
  onView,
  onPreview,
  onAddToCart,
  onSubscribe,
}) {
  const displayName = doc.displayName || doc.name || `${type} ${index + 1}`;
  const buyPrice = doc.price || '20.00';

  // Free samples open in modal without login; only paid/embeded docs require login for View
  const isFree = !doc.hasPassword || doc.freeDocument || doc.freeSample;
  const viewDisabled = state.canOpen && !isLoggedIn && !isFree;
  const inCart = state.inCart === true;
  const showSubscribeAndBuy = !state.canOpen && doc.hasPassword && !inCart;
  const showSubscribeAndInCart = !state.canOpen && doc.hasPassword && inCart;

  const handleView = (e) => {
    e.stopPropagation();
    if (viewDisabled) return;
    onView(doc);
  };

  const handleCart = (e) => {
    e.stopPropagation();
    navigate('/cart');
  };

  const handleBuy = (e) => {
    e.stopPropagation();
    onAddToCart(doc);
    navigate('/cart');
  };

  const handleSubscribe = (e) => {
    e.stopPropagation();
    trackDocumentClick(doc, {
      source: 'interactive_learning',
      outcome: 'subscribe',
      destinationUrl: '/subscriptions',
    });
    (onSubscribe || (() => navigate('/subscriptions')))();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      style={cardStyle}
      onMouseEnter={handleCardHover(true)}
      onMouseLeave={handleCardHover(false)}
      onKeyDown={(e) => e.key === 'Enter' && state.canOpen && handleView(e)}
    >
      <DocumentIconSvg />
      <div style={infoStyle}>
        <div style={nameStyle}>{displayName}</div>
        <div style={badgesStyle}>
          <span
            style={{
              ...badgeStyle,
              backgroundColor: state.canOpen ? DOCUMENTS_PANEL.freeBadgeBg : DOCUMENTS_PANEL.primaryColor,
              color: state.canOpen ? DOCUMENTS_PANEL.freeBadgeColor : 'white',
            }}
          >
            {state.label}
          </span>
        </div>
      </div>

      <div style={actionsStyle}>
        {state.canOpen && (
          <div style={viewButtonWrapStyle}>
            <button
              type="button"
              onClick={handleView}
              disabled={viewDisabled}
              title={viewDisabled ? 'Login first' : undefined}
              style={{
                ...viewButtonStyle,
                ...(viewDisabled ? disabledButtonStyle : {}),
              }}
              onMouseEnter={(e) => {
                if (!viewDisabled) e.currentTarget.style.backgroundColor = DOCUMENTS_PANEL.primaryHover;
              }}
              onMouseLeave={(e) => {
                if (!viewDisabled) e.currentTarget.style.backgroundColor = DOCUMENTS_PANEL.primaryColor;
              }}
            >
              View
            </button>
            {viewDisabled && (
              <span style={loginHintStyle} title="Login first">
                Login first
              </span>
            )}
          </div>
        )}
        {showSubscribeAndInCart && (
          <>
            <button
              type="button"
              onClick={handleSubscribe}
              style={subscribeButtonStyle}
            >
              Subscribe{subscriptionPrice != null ? ` £${subscriptionPrice}` : ''}
            </button>
            <button type="button" onClick={handleCart} style={viewButtonStyle}>
              In Cart
            </button>
          </>
        )}
        {showSubscribeAndBuy && (
          <>
            <button
              type="button"
              onClick={handleSubscribe}
              style={subscribeButtonStyle}
            >
              Subscribe{subscriptionPrice != null ? ` £${subscriptionPrice}` : ''}
            </button>
            <button type="button" onClick={handleBuy} style={viewButtonStyle}>
              Buy £{buyPrice}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function DocumentIconSvg() {
  return (
    <svg
      style={{
        width: '1.25rem',
        height: '1.25rem',
        flexShrink: 0,
        color: DOCUMENTS_PANEL.primaryColor,
      }}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d={DOCUMENT_ICON_PATH}
      />
    </svg>
  );
}

function handleCardHover(enter) {
  return (e) => {
    e.currentTarget.style.backgroundColor = enter ? DOCUMENTS_PANEL.panelBg : 'white';
    e.currentTarget.style.boxShadow = enter ? '0 4px 8px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.06)';
  };
}

const cardStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  padding: '0.875rem',
  border: `2px solid ${DOCUMENTS_PANEL.borderColor}`,
  borderRadius: '12px',
  backgroundColor: 'white',
  boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const infoStyle = { flex: 1, minWidth: 0 };

const nameStyle = {
  fontSize: '0.875rem',
  fontWeight: '600',
  color: '#1f2937',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const badgesStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  marginTop: '0.25rem',
};

const badgeStyle = {
  fontSize: '0.75rem',
  fontWeight: '600',
  padding: '0.25rem 0.5rem',
  borderRadius: '6px',
};

const actionsStyle = { display: 'flex', gap: '0.5rem', alignItems: 'center' };

const subscribeButtonStyle = {
  padding: '0.5rem 0.75rem',
  fontSize: '0.75rem',
  fontWeight: '600',
  color: DOCUMENTS_PANEL.primaryColor,
  backgroundColor: 'transparent',
  border: `2px solid ${DOCUMENTS_PANEL.primaryColor}`,
  borderRadius: '8px',
  cursor: 'pointer',
  flexShrink: 0,
  transition: 'all 0.2s',
};

const viewButtonStyle = {
  padding: '0.5rem 0.875rem',
  fontSize: '0.75rem',
  fontWeight: '600',
  color: 'white',
  backgroundColor: DOCUMENTS_PANEL.primaryColor,
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  flexShrink: 0,
  transition: 'background-color 0.2s',
};

const disabledButtonStyle = {
  opacity: 0.6,
  cursor: 'not-allowed',
};

const viewButtonWrapStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: '0.25rem',
};

const loginHintStyle = {
  fontSize: '0.65rem',
  color: '#666',
  whiteSpace: 'nowrap',
};
